import React from 'react';
import composeFp from 'lodash/fp/compose';
import { Article } from 't2-graphql-types';
import { NextSeo, ArticleJsonLd } from 'next-seo';
import { useGetUserArticlePoaTimePoints } from '../../components/Navbar/hooks/useGetUserArticlePoaTimePoints';
import { useAuthenticatedUser } from '../../components/Auth/hooks/useAuthenticatedUser';
import Container from '../../components/Container/Container';
import Navbar from '../../components/Navbar';
import { withApolloData } from '../../services/apolloClient/withApolloData';
import { getApolloServerClient, getCookiesForApolloServerClient } from '../../services/apolloClient/server';
import findArticleBySlug from '../../components/Article/queries/findArticleBySlug';
import { ArticleContainer } from '../../components/Article';
import { behindFeatureFlag } from '../../components/FeatureFlags/getServerSidePropsDecorators/behindFeatureFlag';
import { forceSignIn } from '../../components/Auth/getServerSidePropsDecorators/forceSignIn';
import { requirePermission } from '../../components/Auth/getServerSidePropsDecorators/requirePermission';
import { useProofOfAttentionTracker } from '../../services/poa-tracker/useProofOfAttentionTracker';
import CommentsContainer from '../../components/CommentsSection/CommentsContainer';
import { getFeatureFlag } from '../../components/FeatureFlags/getFeatureFlag';
import { useLoginPrompt } from '../../components/Article/hooks/useLoginPrompt';
import { APP_URL } from '../../../config';
import { DEFAULT_ARTICLE_IMAGE } from '../../components/ArticleCard/constants';
import { useNavbarHeight } from '../../components/Navbar/hooks/useNavbarHeight';
import getUserDisplayName from '../../../utils/getUserDisplayName';
import HorizontalSeparator from '../../components/HorizontalSeparator/HorizontalSeparator';
import { getArticlePageUrl } from '../../services/pagesUrl/getArticlePageUrl';
import { ArticleRepliesSection } from '../../components/ArticleRepliesSection/ArticleRepliesSection';
import { logVisit } from '../../services/visits/logVisit';
import { useBreadcrumbHeight } from '../../components/Breadcrumbs/hooks/useBreadcrumbHeight';
import { BreadcrumbsListContainer } from '../../components/Breadcrumbs/BreadcrumbsListContainer';

interface IArticlePageProps {
  articleId: Article['id'];
  articleTitle: Article['title'];
  articleUrl: string;
  articleCoverImageUrl: string;
  articleShortDescription: string;
  articleCreatedAt: string;
  articleLastUpdateDate: string;
  authorId: string;
  authorName: string;
  articleReadtime: string;
  articleStatus: Article['status'];
  articleSlug: string;
}

const ArticlePage: React.FC<IArticlePageProps> = ({
  articleId,
  articleTitle,
  articleUrl,
  articleCoverImageUrl,
  articleShortDescription,
  articleCreatedAt,
  articleLastUpdateDate,
  authorId,
  authorName,
  articleReadtime,
  articleStatus,
}) => {
  const navbarHeight = useNavbarHeight();
  const breadcrumbsHeight = useBreadcrumbHeight();
  const { isAuthenticated, authenticatedUserId } = useAuthenticatedUser();
  const { userArticleTimePoints } = useGetUserArticlePoaTimePoints({
    articleId,
    pollInterval: 60000,
    skip: !isAuthenticated,
  });

  useProofOfAttentionTracker(articleId);
  useLoginPrompt();

  const showCommentsSection = getFeatureFlag('commentsSection');
  const isArticleAuthor = authenticatedUserId === authorId;
  const isArticleDraft = articleStatus === 'draft';
  const showRepliesToArticle = getFeatureFlag('replyToQuote');

  return (
    <>
      <NextSeo
        title={articleTitle || undefined}
        description={articleShortDescription}
        openGraph={{
          type: 'article',
          url: articleUrl,
          title: articleTitle || '',
          description: articleShortDescription,
          images: [
            {
              url: articleCoverImageUrl || `${APP_URL}/images/post-image-placeholder.webp`,
            },
          ],
        }}
      />
      <ArticleJsonLd
        useAppDir={false}
        url={articleUrl}
        title={articleTitle || ''}
        images={[articleCoverImageUrl]}
        datePublished={articleCreatedAt}
        dateModified={articleLastUpdateDate}
        authorName={[
          {
            name: authorName,
          },
        ]}
        description={articleShortDescription}
      />
      <div className="min-h-fitScreen">
        <Navbar
          showFullScreenModeButton
          showShareButton
          userArticleTimePoints={isAuthenticated ? userArticleTimePoints : parseInt(articleReadtime, 10)}
          isArticleAuthor={isArticleAuthor}
          isArticleDraft={isArticleDraft}
        />
        <Container>
          <BreadcrumbsListContainer articleId={articleId} type="post" />
        </Container>
        <Container
          className="relative md:grid-cols-12 md:gap-x-3"
          style={{ paddingTop: navbarHeight + breadcrumbsHeight }}
        >
          <ArticleContainer articleId={articleId} />
          {showCommentsSection && (
            <div className="col-span-full md:col-span-8 md:col-start-3 lg:col-span-6 lg:col-start-4 order-7">
              <div className="mb-16 lg:mb-28">
                <HorizontalSeparator />
              </div>
              <CommentsContainer articleId={articleId} />
            </div>
          )}
          {showRepliesToArticle && (
            <div className="col-span-full lg:col-start-2 lg:col-span-10 order-8">
              <div className="my-10">
                <HorizontalSeparator />
              </div>
              <ArticleRepliesSection articleId={articleId} />
            </div>
          )}
        </Container>
      </div>
    </>
  );
};

export default ArticlePage;

export const getServerSideProps = composeFp(
  ...[
    behindFeatureFlag('articlePage'),
    !getFeatureFlag('isArticlePubliclyAccessible') && forceSignIn,
    !getFeatureFlag('isArticlePubliclyAccessible') && requirePermission('GLOBAL_CAN_ACCESS_APP'),
    withApolloData(ArticlePage),
    logVisit,
  ].filter(Boolean),
)(async ({ req, query }) => {
  const apolloClient = getApolloServerClient({
    cookie: getCookiesForApolloServerClient(req.headers.cookie),
  });

  const { articleSlug } = query || {};
  const articleResponse = await apolloClient.query({
    query: findArticleBySlug,
    variables: {
      articleSlug,
    },
  });
  const article = articleResponse?.data.articles[0] || {};

  if (!article?.id) {
    return {
      notFound: true,
    };
  }

  const articleId = article.id;
  const articleTitle = article.title;
  const articleCoverImageUrl = article.coverImage?.publicUrl || `${APP_URL}${DEFAULT_ARTICLE_IMAGE}`;
  const articleShortDescription = article.shortDescription;
  const articleCreatedAt = article.createdAt;
  const articleLastUpdateDate = article.lastUpdateDate || '';
  const authorId = article.author?.id;
  const authorName = getUserDisplayName(article.author);
  const url = getArticlePageUrl(articleSlug);
  const articleUrl = `${APP_URL}${url}`;
  const articleReadtime = article.readTime;
  const articleStatus = article.status;

  return {
    props: {
      articleId,
      articleTitle,
      articleUrl,
      articleCoverImageUrl,
      articleShortDescription,
      articleCreatedAt,
      articleLastUpdateDate,
      authorId,
      authorName,
      articleReadtime,
      articleStatus,
      articleSlug,
    },
  };
});
